import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { canDecideAutoUse } from "@/components/prompt/auto-use-permission";
import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { PromptDTO, PromptOwnerType } from "@shaarp/common";
import { ChevronLeftIcon } from "lucide-react";
import { FormEvent, useId, useState } from "react";
import { ButtonLoading } from "../button-loading";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";
import { TextareaAutosize } from "../ui/textarea-autosize";
import { useToast, useToastError } from "../ui/use-toast";

export default function DialogCreatePrompt({
  initialContent,
  initialOwnerType,
  onClose,
  onCreated,
  variant = "dialog",
}: {
  initialContent: string;
  initialOwnerType?: PromptOwnerType;
  onClose: () => void;
  onCreated?: (prompt: PromptDTO) => void;
  variant?: "dialog" | "sheet";
}) {
  const toastError = useToastError();
  const api = useApi();
  const { toast } = useToast();
  const { t } = useTranslation();

  const { isManager, isAdmin, isGod, teamSelected } = useTeams();
  const [content, setContent] = useState<string>(initialContent);
  const [ownerType, setOwnerType] = useState<PromptOwnerType>(
    initialOwnerType ?? "user",
  );
  // Off until the author switches it on, whatever the owner type: a tactic
  // reaches the model on its own only by an explicit choice
  // (`docs/decisions/0019`).
  const [isAutoEnabled, setIsAutoEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const autoUseHelperId = useId();
  const autoUseScopeId = useId();
  const autoUseReasonId = useId();

  // Same rule as the edit dialog, which the API enforces either way. Without
  // it here an author who switched it on keeps it on while moving to an owner
  // type they may not decide for, and the save comes back refused with a
  // generic error: nothing on screen names the switch as the reason.
  const canSetAutoUse = canDecideAutoUse({
    ownerType,
    selectedTeamRole: teamSelected?.userRole,
    isPlatformGod: isGod,
  });
  // What the switch shows is what is sent: a disabled switch reading "off"
  // must not carry an "on" the state still holds from before the owner type
  // changed.
  const isAutoEnabledSent = canSetAutoUse && isAutoEnabled;

  // Every explanation shown next to the switch, in reading order.
  const describedBy = [
    autoUseHelperId,
    ownerType === "team" ? autoUseScopeId : null,
    canSetAutoUse ? null : autoUseReasonId,
  ]
    .filter(Boolean)
    .join(" ");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      const { data } = await api.post<PromptDTO>(`/prompt`, {
        prompt: {
          content,
          ownerType,
          isAutoEnabled: isAutoEnabledSent,
        },
      });
      toast({
        variant: "default",
        title: t("prompt.create.success.title"),
        description: t("prompt.create.success.description"),
      });
      onCreated?.(data);
      onClose();
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const formFields = (
    <div className="space-y-5">
      {(isManager || isAdmin) && (
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="ownerType">
            {t("prompt.create.fields.type.label")}
          </Label>
          <p className="text-xs text-gray-500">
            {t("prompt.create.fields.type.helper")}
          </p>
          <Select
            value={ownerType}
            onValueChange={(newValue) =>
              setOwnerType(newValue as PromptOwnerType)
            }
          >
            <SelectTrigger>
              <SelectValue defaultValue="user" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="user">
                  {t("prompt.card.types.user")}
                </SelectItem>
                {(isManager || isAdmin) && (
                  <SelectItem value="team">
                    {t("prompt.card.types.team")}
                  </SelectItem>
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <Label htmlFor="isAutoEnabled">
            {t("prompt.create.fields.autoUse.label")}
          </Label>
          <p id={autoUseHelperId} className="text-xs text-gray-500">
            {t("prompt.create.fields.autoUse.helper")}
          </p>
          {/* The reach, and only when there is one to state: switching a team's
              tactic on commits conversations the author will never see. */}
          {ownerType === "team" && (
            <p id={autoUseScopeId} className="mt-1 text-xs text-warning">
              {t("prompt.create.fields.autoUse.scopeTeam")}
            </p>
          )}
          {!canSetAutoUse && (
            <p id={autoUseReasonId} className="mt-1 text-xs text-muted-foreground">
              {t("prompt.create.fields.autoUse.managerOnly")}
            </p>
          )}
        </div>
        {/* Disabled rather than hidden, as in the edit dialog: the author of a
            team tactic still has to see what the switch would have decided. */}
        <Switch
          id="isAutoEnabled"
          checked={isAutoEnabledSent}
          onCheckedChange={setIsAutoEnabled}
          disabled={!canSetAutoUse}
          aria-describedby={describedBy}
        />
      </div>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor="content">
          {t("prompt.create.fields.content.label")}
        </Label>
        <p className="text-xs text-gray-500">
          {t("prompt.create.fields.content.helper")}
        </p>
        <TextareaAutosize
          id="content"
          placeholder={t("prompt.create.fields.content.placeholder")}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          required
        />
      </div>
    </div>
  );

  if (variant === "sheet") {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="relative flex items-center justify-center px-5 pt-3 pb-3 shrink-0 border-b">
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.back")}
            className="absolute left-3 inline-flex items-center justify-center rounded-full size-9 hover:bg-muted active:bg-muted transition-colors"
          >
            <ChevronLeftIcon className="size-5" />
          </button>
          <DialogTitle className="text-lg font-semibold">
            {t("prompt.create.title")}
          </DialogTitle>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
            {formFields}
          </div>
          <div className="shrink-0 border-t px-5 py-4">
            <ButtonLoading
              isLoading={isLoading}
              type="submit"
              className="w-full"
            >
              {t("prompt.create.buttons.create")}
            </ButtonLoading>
          </div>
        </form>
      </div>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[800px] w-full">
        <DialogHeader>
          <DialogTitle>{t("prompt.create.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <Separator className="my-4" />
          <div className="overflow-auto max-h-[70vh] px-3">{formFields}</div>
          <Separator className="my-4" />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">
                {t("prompt.create.buttons.close")}
              </Button>
            </DialogClose>
            <ButtonLoading isLoading={isLoading} type="submit">
              {t("prompt.create.buttons.create")}
            </ButtonLoading>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
